@file:Suppress("MagicNumber")

package com.nonopn.skydome.common.app.pages.fullscreenmap

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.role
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.nonopn.skywatcher.common.app.res.Res
import com.nonopn.skywatcher.common.app.res.map_fullscreen_close
import com.nonopn.skywatcher.common.app.res.map_fullscreen_open
import org.jetbrains.compose.resources.stringResource
import ovh.plrapps.mapcompose.api.scale
import ovh.plrapps.mapcompose.ui.state.MapState

private val ControlText = Color(0xFF1A2B4A)
private val ControlBorder = Color(0xFFCCCCCC)

/**
 * Wraps a map so that it can be opened in a full screen dialog.
 *
 * The same [content] is drawn either inline or in the dialog (never both at once), so a [MapState] shared by the
 * caller keeps its zoom and center. Back (Android) and Escape (web) dismiss the dialog without leaving the screen.
 *
 * @param controlsAlignment where the control column (caller controls + full screen button) sits in the inline map
 * @param closeAlignment where the close button (and caller controls) sits in the full screen dialog
 * @param controls extra buttons shown above the full screen button, receives true when in full screen
 * @param fullscreenOverlay drawn above the map in the dialog only (legend, ...)
 * @param content the map itself, receives true when drawn in the dialog (e.g. to enable gestures)
 */
@Composable
fun FullscreenMapContainer(
    modifier: Modifier = Modifier,
    controlsAlignment: Alignment = Alignment.TopEnd,
    closeAlignment: Alignment = Alignment.TopEnd,
    controlsPadding: Dp = 8.dp,
    controls: @Composable ColumnScope.(isFullscreen: Boolean) -> Unit = {},
    fullscreenOverlay: @Composable BoxScope.() -> Unit = {},
    content: @Composable BoxScope.(isFullscreen: Boolean) -> Unit,
) {
    var fullscreen by remember { mutableStateOf(false) }
    val openLabel = stringResource(Res.string.map_fullscreen_open)
    val closeLabel = stringResource(Res.string.map_fullscreen_close)

    // The inline box keeps its size while the dialog is open
    Box(modifier) {
        if (!fullscreen) {
            content(false)
            Column(
                Modifier.align(controlsAlignment).padding(controlsPadding),
                verticalArrangement = Arrangement.spacedBy(6.dp)
            ) {
                controls(false)
                MapControlIconButton(description = openLabel, onClick = { fullscreen = true })
            }
        }
    }
    if (fullscreen) {
        Dialog(
            onDismissRequest = { fullscreen = false },
            properties = DialogProperties(
                usePlatformDefaultWidth = false,
                dismissOnBackPress = true,
                dismissOnClickOutside = false,
            ),
        ) {
            Box(Modifier.fillMaxSize().background(Color.Black)) {
                content(true)
                Column(
                    Modifier.align(closeAlignment).padding(controlsPadding),
                    verticalArrangement = Arrangement.spacedBy(6.dp)
                ) {
                    MapControlButton("\u00d7", description = closeLabel, onClick = { fullscreen = false })
                    controls(true)
                }
                fullscreenOverlay()
            }
        }
    }
}

/** Zoom buttons (and an optional recenter button) in the same style as the full screen button. */
@Composable
fun MapZoomControls(mapState: MapState, onRecenter: (() -> Unit)? = null) {
    MapControlButton("+", onClick = { mapState.scale *= 1.5 })
    MapControlButton("\u2212", onClick = { mapState.scale /= 1.5 })
    if (onRecenter != null) {
        MapControlButton("\u2316", onClick = onRecenter)
    }
}

@Composable
fun MapControlButton(label: String, description: String? = null, onClick: () -> Unit) {
    Box(
        Modifier
            .size(32.dp)
            .clip(RoundedCornerShape(6.dp))
            .background(Color.White.copy(alpha = 0.9f))
            .border(1.dp, ControlBorder, RoundedCornerShape(6.dp))
            .semantics {
                role = Role.Button
                if (description != null) contentDescription = description
            }
            .clickable { onClick() },
        contentAlignment = Alignment.Center
    ) {
        Text(label, fontSize = 16.sp, fontWeight = FontWeight.Bold, color = ControlText)
    }
}

// Same look as MapControlButton, with a drawn "expand" glyph (four corners)
@Composable
private fun MapControlIconButton(description: String, onClick: () -> Unit) {
    Box(
        Modifier
            .size(32.dp)
            .clip(RoundedCornerShape(6.dp))
            .background(Color.White.copy(alpha = 0.9f))
            .border(1.dp, ControlBorder, RoundedCornerShape(6.dp))
            .semantics {
                role = Role.Button
                contentDescription = description
            }
            .clickable { onClick() },
        contentAlignment = Alignment.Center
    ) {
        Canvas(Modifier.size(16.dp)) {
            val w = size.width
            val arm = w * 0.38f
            val stroke = 2.dp.toPx()
            val color = ControlText
            // top-left, top-right, bottom-left, bottom-right corners
            drawLine(color, Offset(0f, 0f), Offset(arm, 0f), stroke, StrokeCap.Round)
            drawLine(color, Offset(0f, 0f), Offset(0f, arm), stroke, StrokeCap.Round)
            drawLine(color, Offset(w, 0f), Offset(w - arm, 0f), stroke, StrokeCap.Round)
            drawLine(color, Offset(w, 0f), Offset(w, arm), stroke, StrokeCap.Round)
            drawLine(color, Offset(0f, w), Offset(arm, w), stroke, StrokeCap.Round)
            drawLine(color, Offset(0f, w), Offset(0f, w - arm), stroke, StrokeCap.Round)
            drawLine(color, Offset(w, w), Offset(w - arm, w), stroke, StrokeCap.Round)
            drawLine(color, Offset(w, w), Offset(w, w - arm), stroke, StrokeCap.Round)
        }
    }
}
